import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '../components/ui/Badge.jsx';
import { Button } from '../components/ui/Button.jsx';
import { MetricCard } from '../components/ui/Card.jsx';
import { Skeleton } from '../components/ui/Skeleton.jsx';
import { useTranslation } from '../i18n/useTranslation.js';
import { eventLabel } from '../utils/labels.js';
import { automationApi } from '../services/automationApi.js';
import { hrApi } from '../services/hrApi.js';
import { inventoryApi } from '../services/inventoryApi.js';
import { projectsApi } from '../services/projectsApi.js';
import { formatCurrency, formatDate } from '../utils/formatters.js';

// One place per pending source: how to fetch it, where it deep-links, and how
// approve/reject map onto the existing module endpoints.
const ORDER = ['po', 'req', 'leave', 'expense', 'alert'];
const META = {
  po: { to: '/inventory/purchase-orders', severity: 'warning' },
  req: { to: '/inventory/requisitions', severity: 'warning' },
  leave: { to: '/hr/leave', severity: 'info' },
  expense: { to: '/projects/expenses', severity: 'warning' },
  alert: { to: '/automation/alerts', severity: 'info' },
};
const ACTORS = {
  po: { approve: (id) => inventoryApi.approvePO(id), reject: (id) => inventoryApi.rejectPO(id) },
  req: { approve: (id) => inventoryApi.approveRequisition(id), reject: (id) => inventoryApi.rejectRequisition(id) },
  leave: { approve: (id) => hrApi.decideLeave(id, 'APPROVE'), reject: (id) => hrApi.decideLeave(id, 'REJECT') },
  expense: { approve: (id) => projectsApi.setExpenseStatus(id, 'APPROVED'), reject: (id) => projectsApi.setExpenseStatus(id, 'REJECTED') },
  alert: { read: (id) => automationApi.markRunRead(id) },
};
const SEV = { INFO: 'info', WARNING: 'warning', CRITICAL: 'danger' };

export function InboxPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const sources = useQueries({
    queries: [
      { queryKey: ['inbox', 'po'], queryFn: () => inventoryApi.listPurchaseOrders({ status: 'PENDING_APPROVAL' }).then((r) => r.items), retry: false },
      { queryKey: ['inbox', 'req'], queryFn: () => inventoryApi.listRequisitions({ status: 'SUBMITTED' }).then((r) => r.items), retry: false },
      { queryKey: ['inbox', 'leave'], queryFn: () => hrApi.listLeaveRequests({ status: 'PENDING' }).then((r) => r.items), retry: false },
      { queryKey: ['inbox', 'expense'], queryFn: () => projectsApi.listExpenses({ status: 'OPEN' }).then((r) => r.items), retry: false },
      { queryKey: ['inbox', 'alert'], queryFn: () => automationApi.listRuns({ unreadOnly: true }).then((r) => r.items), retry: false },
    ],
  });
  const [po, req, leave, expense, alerts] = sources;
  const loading = sources.some((s) => s.isLoading);

  const act = useMutation({
    mutationFn: ({ kind, action, id }) => ACTORS[kind][action](id),
    onMutate: () => setError(null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inbox'] }),
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  const byKind = {
    po: (po.data || []).map((p) => ({ kind: 'po', id: p.id, title: `${t('inbox.kind.po')} ${p.poNumber}`, subtitle: formatCurrency(p.totalAmount) })),
    req: (req.data || []).map((r) => ({ kind: 'req', id: r.id, title: `${t('inbox.kind.req')} ${r.requisitionNumber}`, subtitle: r.justification || r.notes || '-' })),
    leave: (leave.data || []).map((l) => ({ kind: 'leave', id: l.id, title: `${l.employeeName || ''} · ${l.leaveTypeName || ''}`.trim(), subtitle: `${formatDate(l.startDate)} - ${formatDate(l.endDate)} · ${l.days} ${t('inbox.days')}` })),
    expense: (expense.data || []).map((e) => ({ kind: 'expense', id: e.id, title: e.description || t('inbox.kind.expense'), subtitle: `${formatCurrency(e.amount)}${e.employeeName ? ` · ${e.employeeName}` : ''}` })),
    alert: (alerts.data || []).map((a) => ({ kind: 'alert', id: a.id, title: a.message, subtitle: eventLabel(t, a.triggerEvent), sev: SEV[a.severity] || 'info' })),
  };
  const total = ORDER.reduce((n, k) => n + byKind[k].length, 0);

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('inbox.title')}</h1>
          <p className="muted">{t('inbox.subtitle')}</p>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('inbox.metric.total')} value={String(total)} trendVariant={total > 0 ? 'warning' : 'success'} />
        <MetricCard label={t('inbox.kind.po')} value={String(byKind.po.length)} />
        <MetricCard label={t('inbox.kind.leave')} value={String(byKind.leave.length)} />
        <MetricCard label={t('inbox.kind.expense')} value={String(byKind.expense.length)} />
        <MetricCard label={t('inbox.kind.alert')} value={String(byKind.alert.length)} />
      </div>

      {error && <div className="alert danger">{error}</div>}

      {loading ? (
        <Skeleton variant="table" count={5} />
      ) : total === 0 ? (
        <div className="empty-state"><p>{t('inbox.empty')}</p></div>
      ) : (
        ORDER.filter((k) => byKind[k].length > 0).map((kind) => (
          <div key={kind} style={{ marginBottom: 'var(--space-5)' }}>
            <div className="settings-divider" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{t(`inbox.section.${kind}`)} · {byKind[kind].length}</span>
              <Link className="text-sm" to={META[kind].to}>{t('inbox.viewAll')} →</Link>
            </div>
            <div className="inbox-list">
              {byKind[kind].map((item) => (
                <div key={item.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-2) var(--space-3)', marginBottom: 'var(--space-2)' }}>
                  <Badge variant={item.sev || META[kind].severity}>{t(`inbox.kind.${kind}`)}</Badge>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</div>
                    <div className="muted text-sm">{item.subtitle}</div>
                  </div>
                  {kind === 'alert' ? (
                    <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => act.mutate({ kind, action: 'read', id: item.id })}>{t('inbox.action.read')}</Button>
                  ) : (
                    <>
                      <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => act.mutate({ kind, action: 'reject', id: item.id })}>{t('inbox.action.reject')}</Button>
                      <Button size="sm" variant="primary" disabled={act.isPending} onClick={() => act.mutate({ kind, action: 'approve', id: item.id })}>{t('inbox.action.approve')}</Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </section>
  );
}
